import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  CreditCard, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  RefreshCw,
  Building,
  Wifi,
} from 'lucide-react';
import SEO from '@/components/SEO';
import PaymentDetailDialog from '@/components/admin/payments/PaymentDetailDialog';
import CancelPaymentDialog from '@/components/admin/payments/CancelPaymentDialog';

import {
  getPayments,
  getPaymentStats,
  formatCurrency,
  getStatusStyle,
  PAYMENT_STATUS_OPTIONS,
  type Payment,
} from '@/lib/payments-service';
import { useAuth } from '@/contexts/AuthContext';

const AdminPayments = () => {
  const { role } = useAuth();
  const canEdit = role === 'superadmin' || role === 'admin';
  
  const [payments, setPayments] = useState<Payment[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    successfulCount: 0,
    pendingCount: 0,
    failedCount: 0,
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 15;

  // Detail dialog
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Cancel dialog
  const [selectedPaymentForCancel, setSelectedPaymentForCancel] = useState<Payment | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);


  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [page, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      fetchPayments();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const result = await getPayments({
        search,
        status: statusFilter,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page,
        pageSize,
      });
      setPayments(result.data);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchStats = async () => {
    try {
      const statsData = await getPaymentStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewDetail = (paymentId: string) => {
    setSelectedPaymentId(paymentId);
    setDetailOpen(true);
  };

  const handleCancelPayment = (payment: Payment) => {
    setSelectedPaymentForCancel(payment);
    setCancelDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchPayments();
    fetchStats();
  };

  
  // Check if a payment can be cancelled
  const canCancelPayment = (payment: Payment) => {
    if (!canEdit) return false;
    // Can cancel: pending payments, offline payments, or non-stripe payments
    const isPending = payment.payment_status === 'pending' || payment.payment_status === 'pending_verification';
    const isOffline = payment.payment_source === 'offline';
    const isCancelled = payment.payment_status === 'cancelled';
    return !isCancelled && (isPending || isOffline);
  };


  const getStatusBadgeVariant = (status: string | null) => {
    const style = getStatusStyle(status);
    switch (style.variant) {
      case 'success':
        return 'default';
      case 'warning':
        return 'secondary';
      case 'destructive':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'paid':
      case 'confirmed':
      case 'completed':
        return <CheckCircle className="h-3 w-3" />;
      case 'pending':
        return <Clock className="h-3 w-3" />;
      case 'pending_verification':
        return <AlertCircle className="h-3 w-3" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="h-3 w-3" />;
      default:
        return null;
    }
  };

  return (
    <>
      <SEO 
        title="Payments - Admin"
        description="Manage payments and transactions"
      />
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Payments</h1>
            <p className="text-muted-foreground">
              View and track all payment transactions
            </p>
          </div>
          <Button variant="outline" onClick={handleRefresh} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${stats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Month</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${stats.monthlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">Current month</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Successful</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.successfulCount}</div>
              <p className="text-xs text-muted-foreground">Completed payments</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pendingCount}</div>
              <p className="text-xs text-muted-foreground">Awaiting payment</p>
            </CardContent>
          </Card>
        </div>


        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email, booking ID, or Stripe ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {PAYMENT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-[150px]"
                placeholder="From"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-[150px]"
                placeholder="To"
              />
            </div>
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
            <CardDescription>
              {total} total payments found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No payments found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Payments will appear here when bookings are made'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Booking ID</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(payment.created_at), 'MMM d, yyyy')}
                            <br />
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(payment.created_at), 'h:mm a')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{payment.full_name}</div>
                            <div className="text-sm text-muted-foreground">{payment.email}</div>
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">
                            {payment.service}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(payment.payment_amount, payment.payment_currency || 'USD')}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm capitalize">
                              {payment.payment_method || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm">
                              {payment.payment_source === 'offline' ? (
                                <>
                                  <Building className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span>Offline</span>
                                </>
                              ) : payment.stripe_session_id ? (
                                <>
                                  <Wifi className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span>Online</span>
                                </>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={getStatusBadgeVariant(payment.payment_status)}
                              className="gap-1"
                            >
                              {getStatusIcon(payment.payment_status)}
                              {getStatusStyle(payment.payment_status).label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1 py-0.5 rounded">
                              {payment.booking_id.substring(0, 12)}...
                            </code>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetail(payment.id)}
                                className="gap-1"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                              {canCancelPayment(payment) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelPayment(payment)}
                                  className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <XCircle className="h-4 w-4" />
                                  Void
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Detail Dialog */}
        <PaymentDetailDialog
          paymentId={selectedPaymentId}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />

        {/* Cancel Payment Dialog */}
        <CancelPaymentDialog
          payment={selectedPaymentForCancel}
          open={cancelDialogOpen}
          onOpenChange={setCancelDialogOpen}
          onPaymentCancelled={handleRefresh}
        />

      </div>
    </>
  );
};

export default AdminPayments;
